import { useEffect, useState } from 'react'
import { api, ROUTES, imgUrl, toFormData } from './api'
import { useAdmin, useAsync, Modal, Field, ImageInput, ErrorState, Loading, Empty, PageHead } from './ui'

// Live preview: shows the banner the way the storefront hero will show it.
function Preview({ b, file }) {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    if (!file) return setUrl(null)
    const u = URL.createObjectURL(file)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [file])
  const bg = url || imgUrl(b.image)
  return (
    <div className="bn-preview" style={bg ? { backgroundImage: `url(${bg})` } : undefined}>
      <div className="bn-shade">
        <h3>{b.title || 'Banner headline'}</h3>
        {b.subtitle && <p>{b.subtitle}</p>}
        {b.button_text && <span className="bn-btn">{b.button_text}</span>}
      </div>
    </div>
  )
}

export default function Banners() {
  const { toast, confirm } = useAdmin()
  const { data, loading, error, reload } = useAsync(() => api.get(ROUTES.adminBannersAll))
  const [editing, setEditing] = useState(null)
  const banners = data || []

  const remove = async (b) => {
    if (!(await confirm({ title: 'Delete banner', body: `Delete "${b.title}"? It will disappear from your storefront.` }))) return
    try {
      await api.del(`${ROUTES.adminBanners}/${b.id}`)
      toast('Banner deleted')
      reload()
    } catch (e) {
      toast(e.message, 'err')
    }
  }

  const toggle = async (b) => {
    try {
      await api.upload('PUT', `${ROUTES.adminBanners}/${b.id}`, toFormData({ ...b, image: null, is_active: b.is_active ? 0 : 1 }))
      reload()
    } catch (e) {
      toast(e.message, 'err')
    }
  }

  if (error) return <ErrorState error={error} endpoint={`GET ${ROUTES.adminBannersAll}`} onRetry={reload} />

  return (
    <>
      <PageHead title="Banners" sub="Hero images, headlines and button text shown on your storefront." actions={<button className="btn primary" onClick={() => setEditing({})}>Add banner</button>} />
      {loading ? <Loading /> : banners.length === 0 ? <Empty title="No banners yet" body="Add a banner to feature a sale or a new collection." /> : (
        <div className="bn-list">
          {banners.map((b) => (
            <article key={b.id} className={`bn${b.is_active ? '' : ' off'}`}>
              <Preview b={b} />
              <div className="bn-foot">
                <span className="muted">Order {b.sort_order ?? 0} · links to {b.button_link || 'nowhere'}</span>
                <div className="actions">
                  <label className="switch"><input type="checkbox" checked={!!b.is_active} onChange={() => toggle(b)} /> {b.is_active ? 'Live' : 'Hidden'}</label>
                  <button className="btn sm" onClick={() => setEditing(b)}>Edit</button>
                  <button className="btn sm danger-ghost" onClick={() => remove(b)}>Delete</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
      {editing && <BannerForm banner={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload() }} />}
    </>
  )
}

function BannerForm({ banner, onClose, onSaved }) {
  const { toast } = useAdmin()
  const [f, setF] = useState({
    title: banner?.title ?? '',
    subtitle: banner?.subtitle ?? '',
    button_text: banner?.button_text ?? '',
    button_link: banner?.button_link ?? '',
    sort_order: banner?.sort_order ?? 0,
    is_active: banner ? !!banner.is_active : true,
  })
  const [file, setFile] = useState(null)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })

  const submit = async (e) => {
    e.preventDefault()
    const er = {}
    if (!f.title.trim()) er.title = 'Enter a headline'
    if (!banner && !file) er.image = 'Choose a banner image'
    if (f.button_text && !f.button_link) er.button_link = 'Add a link for the button'
    setErrors(er)
    if (Object.keys(er).length) return
    setSaving(true)
    try {
      const form = toFormData({ ...f, is_active: f.is_active ? 1 : 0, image: file })
      if (banner) await api.upload('PUT', `${ROUTES.adminBanners}/${banner.id}`, form)
      else await api.upload('POST', ROUTES.adminBanners, form)
      toast(banner ? 'Banner saved' : 'Banner added')
      onSaved()
    } catch (err) {
      toast(err.message, 'err')
      setSaving(false)
    }
  }

  return (
    <Modal
      wide
      title={banner ? 'Edit banner' : 'Add banner'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" form="bn-form" disabled={saving}>{saving ? 'Saving…' : banner ? 'Save changes' : 'Add banner'}</button>
        </>
      }
    >
      <Preview b={{ ...f, image: banner?.image }} file={file} />
      <form id="bn-form" onSubmit={submit} className="form-grid" noValidate style={{ marginTop: 16 }}>
        <div className="span-2"><Field label="Headline" error={errors.title}><input className="input" value={f.title} onChange={set('title')} /></Field></div>
        <div className="span-2"><Field label="Supporting text"><input className="input" value={f.subtitle} onChange={set('subtitle')} /></Field></div>
        <Field label="Button text" hint="Leave empty for no button."><input className="input" value={f.button_text} onChange={set('button_text')} placeholder="Shop the sale" /></Field>
        <Field label="Button link" error={errors.button_link} hint="For example /shop or /product-category/3"><input className="input" value={f.button_link} onChange={set('button_link')} /></Field>
        <Field label="Display order" hint="Lower numbers show first."><input className="input" type="number" value={f.sort_order} onChange={set('sort_order')} /></Field>
        <label className="check"><input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} /> Show on storefront</label>
        <div className="span-2">
          <span className="field-label">Image</span>
          <ImageInput current={banner?.image} file={file} onChange={setFile} />
          {errors.image && <span className="err">{errors.image}</span>}
        </div>
      </form>
    </Modal>
  )
}
